/**
 * AttendanceList — this event's attendees, on the event detail page.
 *
 * Figma `1:9871` (y 12045), the third card. The designed columns, `Review` and `Export CSV` in the
 * header, a search box and pagination.
 *
 * Fed from `ListEventAttendees`: every nomination for the event first, then the Customer Link
 * campaign members of its SFDC campaign — a member the service matched to a nomination takes that
 * nomination's row (utils/attendees `mergeAttendees`). The service decides what each role sees. So
 * a row is a person, not necessarily a nomination — someone who registered without being nominated has no BD, no review status and no
 * Attendance Confirmed, and reads as dashes there (see utils/attendees for how each column is read).
 *
 * Registration Status / Attendance Status come from the campaign member status: Registration
 * reads it as registered or not, Attendance shows it exactly as SFDC holds it. Account ID /
 * Account Name are the member's SFDC account. Job Title and Job Role are the member's own
 * Customer Link values, falling back to the nomination's.
 *
 * Headers and values are all English, unlike the Figma's mixed 嘉宾 / 企业 / 提名审核 — the same
 * names the Nomination List uses, so one field reads the same on both pages.
 *
 * Filters sit beside the search, each labelled above it like the Nomination List's, so an "All"
 * dropdown says what it filters: Nomination Status, defaulting to "All but rejected" (a rejected
 * nomination with no campaign member behind it is hidden until the reader asks for it, see
 * utils/attendees `matchesStatusFilter`), then Customer Business Unit (the Customer BU column:
 * the member's territory sub BU, else the nomination's — not the BD's), Registration Status, Attendance
 * Confirmed and Attendance Status (utils/attendees `matchesAttendeeFilters`). The counter counts
 * what the dropdowns admit; the search's matches are counted in the search box.
 *
 * Two notes can sit under the title. While later pages are still loading, the counter reads
 * "(N+)" and a line says the count and Export CSV cover only what has loaded. For a reader the
 * service scopes (a BD: their territories' members and their own and reports' nominations), a line
 * says so — their count is their slice, not the event's, and a guest another BD nominated reads
 * as un-nominated to them.
 *
 * With sub-campaigns on (SUB_CAMPAIGN_ATTENDEES_ENABLED, config.ts) the rows also cover the
 * campaigns beneath the event, merged into one row per person (utils/attendeeMerger). Campaigns
 * then lists the name of each campaign the person is a member of, the event behind it a link, and
 * SFDC Campaign ID their ids; both are hidden by default, in the preferences. The nomination
 * columns show the person's preferred nomination across all of them.
 *
 * Only Attended and Registered campaign members are listed (utils/attendeeMerger); Attendance
 * Status shows which, or a dash for a nomination with no campaign member behind it. Sorting waits for
 * the last page, as the count does: a person on page 1 can still gain memberships from page 5.
 *
 * Columns can be hidden and reordered through the same Cloudscape preferences modal the
 * Nomination List offers, stored per browser (utils/tablePreferences). Comments starts hidden:
 * it is the reject reason, empty on most rows, and the widest column.
 *
 * `Review` hands off to the Nomination List page filtered to this event rather than deciding
 * here. Approve / Reject already live there; a second entry point would be a second chance for
 * the two to disagree. It is shown only when the page passes `onReview`: an event that takes no
 * nominations has nothing to review.
 *
 * Presentational: the page fetches, and decides what Review, Export CSV and a guest-name click
 * do. A plain click on a nominated guest's name is handed to the page, which opens the nomination
 * modal in place; the link's href is still the Nomination List with that nomination open, so a
 * new tab or a copied link lands somewhere that shows it.
 */

import { useCallback, useMemo, useState } from 'react';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import CollectionPreferences, {
  type CollectionPreferencesProps,
} from '@amzn/awsui-components-react/polaris/collection-preferences';
import Header from '@amzn/awsui-components-react/polaris/header';
import Link from '@amzn/awsui-components-react/polaris/link';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import Select, { type SelectProps } from '@amzn/awsui-components-react/polaris/select';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import StatusIndicator from '@amzn/awsui-components-react/polaris/status-indicator';
import Table, { type TableProps } from '@amzn/awsui-components-react/polaris/table';
import TextFilter from '@amzn/awsui-components-react/polaris/text-filter';
import { useCollection } from '@amzn/awsui-collection-hooks';
import type { EventAttendee, Nomination } from '../../client/EventCalendarClient';
import { nominationHref } from '../../utils/nominationLink';
import { AttendanceToggle } from '../AttendanceToggle';
import {
  NOMINATION_STATUS_INDICATOR,
  NOMINATION_STATUS_LABELS,
} from '../../utils/nominationStatus';
import { canonicalJobTitle } from '../../utils/jobTitles';
import { loadPreferences, savePreferences } from '../../utils/tablePreferences';
import {
  attendanceFilterOptions,
  attendanceStatus,
  attendeeMemberships,
  campaignIdsCellText,
  campaignsCellText,
  membershipText,
  attendeeAccountId,
  attendeeAccountName,
  attendeeEmail,
  attendeeCellText,
  attendeeJobRole,
  attendeeJobTitle,
  attendeeKey,
  attendeeName,
  attendeePhone,
  ATTENDEE_FILTER_ALL,
  ATTENDEE_STATUS_FILTERS,
  buFilterOptions,
  matchesAttendeeFilters,
  matchesStatusFilter,
  NO_ATTENDEE_FILTERS,
  registrationFilterOptions,
  registrationStatus,
  type AttendeeFilters,
  type AttendeeStatusFilter,
} from '../../utils/attendees';
import { ATTENDANCE_CONFIRMED, ATTENDANCE_UNCONFIRMED } from '../../utils/nominationCounts';
import {
  columnLabel,
  columnsFor,
  defaultContentDisplay,
} from '../../utils/nominationColumns';
import '../EventFilters/EventFilters.css';

const DASH = '—';

const ALL_OPTION: SelectProps.Option = { value: ATTENDEE_FILTER_ALL, label: 'All' };

const CONFIRMED_OPTIONS: SelectProps.Option[] = [
  ALL_OPTION,
  { value: ATTENDANCE_CONFIRMED, label: 'Confirmed' },
  { value: ATTENDANCE_UNCONFIRMED, label: 'Not confirmed' },
];

/** The option for `value`, or All when the rows no longer carry it. */
function selected(options: readonly SelectProps.Option[], value: string): SelectProps.Option {
  return options.find((o) => o.value === value) ?? ALL_OPTION;
}

/** The preferences modal's column list and defaults — shared with the Nomination List. */
const COLUMN_OPTIONS = columnsFor('attendance').map(({ id, label }) => ({ id, label }));

/**
 * Versioned like the Nomination List's: bump when the column set or the defaults change, so a
 * saved layout from before does not hide the new columns. v2 added Phone / Email and the #34
 * defaults; v3 took the shared column list (utils/nominationColumns). Not bumped for Campaigns:
 * it is hidden by default, and Cloudscape offers a column missing from a saved layout in the
 * modal, unticked — so a v3 layout already reads it as hidden and nobody's choices reset.
 */
const PREFERENCES_PAGE = 'attendanceList.v3';

const DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences = {
  pageSize: 10,
  contentDisplay: defaultContentDisplay('attendance'),
};

export interface AttendanceListProps {
  /** This event's attendees (ListEventAttendees). */
  attendees: readonly EventAttendee[];
  /** The first page has not arrived: the table shows its loading state. */
  loading?: boolean;
  /** Rows are showing, but later pages are still loading: the count and export are partial. */
  loadingMore?: boolean;
  /**
   * The service scoped this reader's view (a BD): the rows are their slice of the event, and the
   * header says so.
   */
  scoped?: boolean;
  /** Hands off to the review queue for this event. Without it there is no Review button. */
  onReview?: () => void;
  /**
   * Opens one nomination in full, on a plain click of a nominated guest's name. Without it the
   * link is an ordinary page load of its href.
   */
  onSelectNomination?: (nomination: Nomination) => void;
  /** The login Event Hub acts as — decides who may tick 确认出席 (see AttendanceToggle). */
  login?: string;
  /** Receives a nomination the service just updated (an attendance confirmation). */
  onNominationUpdated?: (updated: Nomination) => void;
  /**
   * Exports the rows given — what passed the search, not the whole event and not the current
   * page. Someone who filtered to one BD means that BD, and a file of the ten rows that
   * happened to be on screen would be a surprise. The set is passed out because the search
   * lives in here.
   */
  onExportCsv: (attendees: readonly EventAttendee[]) => void;
  /** This event: in the Campaigns column, its own campaign is not a link to itself. */
  eventId?: string;
  /** Opens another event, from the Campaigns column. Without it campaign names are plain text. */
  onOpenEvent?: (eventId: string) => void;
}

export default function AttendanceList({
  attendees,
  loading,
  loadingMore,
  scoped,
  onReview,
  onSelectNomination,
  login,
  onNominationUpdated,
  onExportCsv,
  eventId,
  onOpenEvent,
}: AttendanceListProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<AttendeeStatusFilter>('ACTIVE');
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>(() =>
    loadPreferences(PREFERENCES_PAGE, DEFAULT_PREFERENCES),
  );
  const [filters, setFilters] = useState<AttendeeFilters>(NO_ATTENDEE_FILTERS);
  /* Rows whose attendance the reader toggled since picking an Attendance Confirmed value: kept
     visible under it (utils/attendees `matchesAttendeeFilters`). */
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const pageSize = preferences.pageSize ?? 10;

  const setFilter = (key: keyof AttendeeFilters, value: string) => {
    setFilters((f) => ({ ...f, [key]: value }));
    if (key === 'confirmed') setTouched(new Set());
  };

  const onAttendanceUpdated = useCallback(
    (updated: Nomination) => {
      if (updated.nominationId) {
        const id = updated.nominationId;
        setTouched((t) => new Set(t).add(id));
      }
      onNominationUpdated?.(updated);
    },
    [onNominationUpdated],
  );

  const buOptions = useMemo(() => [ALL_OPTION, ...buFilterOptions(attendees)], [attendees]);
  const registrationOptions = useMemo(
    () => [ALL_OPTION, ...registrationFilterOptions(attendees)],
    [attendees],
  );
  const attendanceOptions = useMemo(
    () => [ALL_OPTION, ...attendanceFilterOptions(attendees)],
    [attendees],
  );

  /* A value the rows no longer carry (the list reloaded) reads, and filters, as All. */
  const effectiveFilters = useMemo<AttendeeFilters>(
    () => ({
      ...filters,
      bu: String(selected(buOptions, filters.bu).value),
      registration: String(selected(registrationOptions, filters.registration).value),
      attendance: String(selected(attendanceOptions, filters.attendance).value),
    }),
    [filters, buOptions, registrationOptions, attendanceOptions],
  );

  const byStatus = useMemo(
    () =>
      attendees.filter(
        (a) =>
          matchesStatusFilter(a, statusFilter) && matchesAttendeeFilters(a, effectiveFilters, touched),
      ),
    [attendees, statusFilter, effectiveFilters, touched],
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return byStatus;
    return byStatus.filter((a) =>
      [
        attendeeName(a),
        attendeeAccountName(a),
        attendeeAccountId(a),
        canonicalJobTitle(attendeeJobTitle(a)),
        canonicalJobTitle(attendeeJobRole(a)),
        attendeePhone(a),
        attendeeEmail(a),
        a.nomination?.bdName,
        a.nomination?.createdBy,
        a.nomination?.rejectReason,
        registrationStatus(a),
        campaignsCellText(a),
        campaignIdsCellText(a),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [byStatus, search]);

  /* Memoised on the callback alone: useCollection tracks the sorted column by reference, so
     rebuilding these on every render makes sorting stop working after one click. */
  const columnDefinitions = useMemo<TableProps.ColumnDefinition<EventAttendee>[]>(
    () => [
      /* The guest comes first and, when nominated, is the way into the nomination modal: who it
         is is what a reader scans for. The href still points at the Nomination List with it
         open, for a new tab or a copied link. */
      {
        id: 'guest',
        header: 'Contact Name',
        sortingComparator: (a, b) => (attendeeName(a) ?? '').localeCompare(attendeeName(b) ?? ''),
        cell: (a) => {
          const name = attendeeName(a) || '(unnamed)';
          const n = a.nomination;
          if (!n?.nominationId) return name;
          return (
            <Link
              href={nominationHref(n.nominationId, n.eventId)}
              onFollow={(e) => {
                if (!onSelectNomination) return;
                e.preventDefault();
                onSelectNomination(n);
              }}
            >
              {name}
            </Link>
          );
        },
      },
      {
        id: 'sfdcAccountId',
        header: columnLabel('sfdcAccountId'),
        // The member's SFDC account, else the nomination's.
        cell: (a) => attendeeAccountId(a) || DASH,
      },
      {
        id: 'company',
        header: columnLabel('company'),
        sortingComparator: (a, b) =>
          (attendeeAccountName(a) ?? '').localeCompare(attendeeAccountName(b) ?? ''),
        cell: (a) => attendeeAccountName(a) || DASH,
      },
      /* canonicalJobTitle shows the job-role list's English key for a value that is one of its
         zh-CN labels; anything else (Customer Link free text) is shown as given. */
      {
        id: 'jobRole',
        header: 'Job Role',
        cell: (a) => canonicalJobTitle(attendeeJobRole(a)) || DASH,
      },
      {
        id: 'jobTitle',
        header: 'Job Title',
        cell: (a) => canonicalJobTitle(attendeeJobTitle(a)) || DASH,
      },
      {
        id: 'phone',
        header: 'Phone',
        cell: (a) => attendeePhone(a) || DASH,
      },
      {
        id: 'email',
        header: 'Email',
        cell: (a) => attendeeEmail(a) || DASH,
      },
      {
        id: 'bdName',
        header: 'BD Name',
        // Falls back to the login: bdName comes from a profile service and can be absent.
        cell: (a) => a.nomination?.bdName || a.nomination?.createdBy || DASH,
      },
      {
        id: 'status',
        header: 'Nomination Status',
        cell: (a) =>
          a.nomination?.status ? (
            <StatusIndicator type={NOMINATION_STATUS_INDICATOR[a.nomination.status]}>
              {NOMINATION_STATUS_LABELS[a.nomination.status]}
            </StatusIndicator>
          ) : (
            <Box color="text-status-inactive">{DASH}</Box>
          ),
      },
      {
        id: 'registration',
        header: 'Registration Status',
        cell: (a) => registrationStatus(a) || DASH,
      },
      {
        id: 'confirmed',
        header: 'Attendance Confirmed',
        minWidth: 170,
        // The nominating BD ticks it on an approved nomination; everyone else reads it. Nothing
        // to confirm for someone who was not nominated.
        cell: (a) =>
          a.nomination ? (
            <AttendanceToggle
              nomination={a.nomination}
              login={login}
              onUpdated={onAttendanceUpdated}
            />
          ) : (
            <Box color="text-status-inactive">{DASH}</Box>
          ),
      },
      {
        id: 'attendance',
        header: 'Attendance Status',
        cell: (a) => attendanceStatus(a) || DASH,
      },
      /* One line per campaign, the event's own first. The name links to the Event Hub event
         behind the campaign; an external campaign has none and reads as its name. */
      {
        id: 'campaigns',
        header: columnLabel('campaigns'),
        minWidth: 220,
        cell: (a) => {
          const ms = attendeeMemberships(a);
          if (!ms.length) return DASH;
          return (
            <SpaceBetween size="xxxs">
              {ms.map((m) => {
                const target = m.eventId;
                return (
                  <div key={m.campaignMemberId}>
                    {target && target !== eventId && onOpenEvent ? (
                      <Link
                        href={`/event/events/${target}`}
                        onFollow={(e) => {
                          e.preventDefault();
                          onOpenEvent(target);
                        }}
                      >
                        {membershipText(m)}
                      </Link>
                    ) : (
                      membershipText(m)
                    )}
                  </div>
                );
              })}
            </SpaceBetween>
          );
        },
      },
      {
        id: 'sfdcCampaignId',
        header: columnLabel('sfdcCampaignId'),
        minWidth: 180,
        cell: (a) => {
          const ms = attendeeMemberships(a).filter((m) => m.sfdcCampaignId);
          if (!ms.length) return DASH;
          return (
            <SpaceBetween size="xxxs">
              {ms.map((m) => (
                <div key={m.campaignMemberId}>{m.sfdcCampaignId}</div>
              ))}
            </SpaceBetween>
          );
        },
      },
      /* Plain-text columns, through attendeeCellText so the table and the CSV agree. Customer BU
         is the member's territory sub BU, else the nomination's (utils/attendees `attendeeBu`);
         the rest are read straight off the nomination, as the Nomination List shows them. */
      ...(['accountTags', 'customerBu', 'bdPhone', 'submittedOn', 'reviewedBy', 'comments'] as const).map(
        (id): TableProps.ColumnDefinition<EventAttendee> => ({
          id,
          header: columnLabel(id),
          minWidth: id === 'comments' ? 160 : undefined,
          cell: (a) => attendeeCellText(id, a) || DASH,
        }),
      ),
    ],
    [onSelectNomination, login, onAttendanceUpdated, eventId, onOpenEvent],
  );

  const { items, collectionProps, paginationProps } = useCollection(filtered, {
    pagination: { pageSize },
    sorting: {},
  });

  return (
    <Table
      {...collectionProps}
      items={items}
      columnDefinitions={columnDefinitions}
      columnDisplay={preferences.contentDisplay}
      trackBy={attendeeKey}
      /* Sorting across campaigns waits for the last page (see the header comment). */
      sortingDisabled={loadingMore}
      loading={loading}
      loadingText="Loading attendees"
      /* The guest stays in view while the other columns scroll sideways. */
      stickyColumns={{ first: 1, last: 0 }}
      variant="container"
      header={
        <Header
          variant="h2"
          counter={
            loading && attendees.length === 0
              ? undefined
              : `(${byStatus.length}${loadingMore ? '+' : ''})`
          }
          description={
            loadingMore || scoped ? (
              <>
                {scoped ? (
                  <div>
                    Guests in your territories and your team&apos;s nominations only. A guest
                    another BD nominated shows no nomination here.
                  </div>
                ) : null}
                {loadingMore ? (
                  <StatusIndicator type="loading">
                    Loading more… {attendees.length} so far. The count and Export CSV cover only
                    what has loaded.
                  </StatusIndicator>
                ) : null}
              </>
            ) : undefined
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {onReview ? <Button onClick={onReview}>Review</Button> : null}
              {/* The filtered set, not `items` — `items` is one page of it. Disabled when the
                  search matches nothing, which is the same set the export would produce. */}
              <Button onClick={() => onExportCsv(filtered)} disabled={filtered.length === 0}>
                Export CSV
              </Button>
            </SpaceBetween>
          }
        >
          Attendance List
        </Header>
      }
      /* Laid out like the Nomination List's filter bar: each control labelled above it. */
      filter={
        <div className="eh-filters eh-filters--in-table">
          <FormField label="Search">
            <TextFilter
              filteringText={search}
              onChange={({ detail }) => setSearch(detail.filteringText)}
              filteringPlaceholder="Find resources"
              filteringAriaLabel="Find attendees for this event"
              countText={search.trim() ? `${filtered.length} matches` : undefined}
            />
          </FormField>
          <FormField label="Nomination Status">
            <Select
              selectedOption={
                ATTENDEE_STATUS_FILTERS.find((o) => o.value === statusFilter) ?? null
              }
              options={ATTENDEE_STATUS_FILTERS.map((o) => ({ ...o }))}
              onChange={({ detail }) =>
                setStatusFilter(detail.selectedOption.value as AttendeeStatusFilter)
              }
            />
          </FormField>
          <FormField label="Customer Business Unit">
            <Select
              selectedOption={selected(buOptions, filters.bu)}
              options={buOptions}
              onChange={({ detail }) => setFilter('bu', String(detail.selectedOption.value))}
              filteringType="auto"
            />
          </FormField>
          <FormField label="Registration Status">
            <Select
              selectedOption={selected(registrationOptions, filters.registration)}
              options={registrationOptions}
              onChange={({ detail }) =>
                setFilter('registration', String(detail.selectedOption.value))
              }
            />
          </FormField>
          <FormField label="Attendance Confirmed">
            <Select
              selectedOption={selected(CONFIRMED_OPTIONS, filters.confirmed)}
              options={CONFIRMED_OPTIONS}
              onChange={({ detail }) => setFilter('confirmed', String(detail.selectedOption.value))}
            />
          </FormField>
          <FormField label="Attendance Status">
            <Select
              selectedOption={selected(attendanceOptions, filters.attendance)}
              options={attendanceOptions}
              onChange={({ detail }) =>
                setFilter('attendance', String(detail.selectedOption.value))
              }
            />
          </FormField>
        </div>
      }
      pagination={
        filtered.length > pageSize ? (
          <Pagination
            {...paginationProps}
            ariaLabels={{
              nextPageLabel: 'Next page',
              previousPageLabel: 'Previous page',
              pageLabel: (n) => `Page ${n}`,
            }}
          />
        ) : undefined
      }
      preferences={
        <CollectionPreferences
          preferences={preferences}
          onConfirm={({ detail }) => {
            setPreferences(detail);
            savePreferences(PREFERENCES_PAGE, detail);
          }}
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          pageSizePreference={{
            title: 'Page size',
            options: [
              { value: 10, label: '10 attendees' },
              { value: 20, label: '20 attendees' },
              { value: 50, label: '50 attendees' },
            ],
          }}
          contentDisplayPreference={{
            title: 'Column preferences',
            description: 'Customize the columns visibility and order.',
            options: COLUMN_OPTIONS.map((c) => ({
              ...c,
              /* The guest's name is the way into the nomination modal; hiding it would leave
                 no route there. */
              alwaysVisible: c.id === 'guest',
            })),
          }}
        />
      }
      empty={
        <Box textAlign="center" padding={{ vertical: 'l' }}>
          <Box variant="strong" color="text-body-secondary">
            {attendees.length === 0 ? 'Not started yet' : 'No matching attendees'}
          </Box>
          <Box color="text-status-inactive" fontSize="body-s" padding={{ top: 'xxs' }}>
            {attendees.length === 0
              ? 'Attendees appear here once BD nominate guests or guests register.'
              : 'Try a different search or clearing a filter.'}
          </Box>
        </Box>
      }
    />
  );
}
